import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { defaultNavItems } from './navItems';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import '../styles/dashboard.css';
import { Avatar, Dropdown } from 'flowbite-react';
import { fetchUserData } from '../../database/fetch-user-data';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from '../../resources/gcp-config';
import { handleLogout } from '../../auth/auth';

const auth = getAuth(app);

const Sidebar = ({
  collapsed,
  navItems = defaultNavItems,
  shown,
  setCollapsed,
  isCropLoading,
}) => {
  const Icon = collapsed ? ChevronDoubleRightIcon : ChevronDoubleLeftIcon;
  const location = useLocation();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);

  useEffect(() => {
    fetchUserData()
      .then((data) => {
        setUserData(data);
        if (data?.name) {
          localStorage.setItem('name', data.name.split(' ')[0]);
        }
      })
      .catch((error) => {
        console.error('Error fetching user data: ', error);
      });
  }, [isCropLoading]);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        localStorage.setItem('email', user.email);
      }
    });
  }, []);

  return (
    <div
      className={classNames({
        'bg-gray-800 text-zinc-50 relative md:translate-x-0 z-20': true,
        'transition-all duration-300 ease-in-out': true,
        'w-[300px]': !collapsed,
        'w-16': collapsed,
        '-translate-x-full': !shown,
      })}
    >
      <div className="flex flex-col justify-between h-screen sticky inset-0 w-full">
        {/* Logo & Collapse */}
        <div
          className={classNames({
            'flex items-center border-b border-b-gray-700 transition-none': true,
            'p-4 justify-between': !collapsed,
            'py-4 justify-center': collapsed,
          })}
        >
          {!collapsed && (
            <span className="whitespace-nowrap">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/yoda-technologies-kb.appspot.com/o/serving-again-logo.png?alt=media&token=3dd3a271-c210-407f-a952-7d8e26f9ffc0"
                alt="Company Logo"
                className="px-3"
              />
            </span>
          )}
          <button
            className="grid place-content-center hover:bg-gray-700 w-10 h-10 rounded-full"
            onClick={() => setCollapsed(!collapsed)}
          >
            <Icon className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-grow">
          <ul className="my-2 flex flex-col gap-1">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.href;
              return (
                <li
                  key={index}
                  className={classNames({
                    'flex items-center': true,
                    'transition-colors duration-300': true,
                    'rounded-md p-2 mx-3 gap-4 hover:bg-gray-700': !collapsed,
                    'rounded-full p-2 mx-3 w-10 h-10 hover:bg-gray-700': collapsed,
                    'bg-gray-700': isActive,
                  })}
                >
                  <Link to={item.href} className="flex items-center gap-2 w-full">
                    {item.icon}
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Dropdown */}
        <div className={`${!collapsed ? 'mx-3' : 'mx-2'} mb-3`}>
          <Dropdown
            arrowIcon={false}
            inline
            label={
              <div className="flex items-center gap-2">
                <Avatar
                  img={userData?.photoURL || undefined}
                  rounded
                  placeholderInitials={
                    userData?.name
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')
                  }
                />
                {!collapsed && (
                  <span className="text-sm text-white font-medium truncate max-w-[150px]">
                    {userData?.name || 'Loading...'}
                  </span>
                )}
              </div>
            }
          >
            <Dropdown.Header>
              <span className="block text-sm">{userData?.name}</span>
              <span className="block truncate text-sm font-medium">
                {userData?.primaryEmail || localStorage.getItem('email')}
              </span>
            </Dropdown.Header>
            <Dropdown.Item onClick={() => navigate('/dashboard')}>
              Dashboard
            </Dropdown.Item>
            <Dropdown.Item onClick={() => navigate('/profile')}>
              Edit Profile
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={() => handleLogout(navigate)}
              className="text-red-500"
            >
              Sign out
            </Dropdown.Item>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  collapsed: PropTypes.bool,
  navItems: PropTypes.array,
  setCollapsed: PropTypes.func,
  shown: PropTypes.bool,
};

export default Sidebar;
